import { Box, styled } from '@mui/material';
import { Form, Formik } from 'formik';
import { memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import {
    CodeMode,
    LanguageSetting,
    useMutationPatchUpdateUserSettings,
    useQueryGetRefreshAndRetrieveUserConfiguration,
} from '../../backend';
import { errorToastConfig, successToastConfig } from '../../config/constants';
import { useCurrentUser, useRenewToken } from '../../contexts/OidcContext/OidcContext.selectors';
import { SettingsForm } from './SettingsForm';

const StyledSettingsContainer = styled(Box)`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    overflow: scroll;
`;

const StyledSettingsForm = styled(Box)`
    color: white;
    margin-top: 100px;
    width: 950px;
`;

export interface UserFormData {
    language: LanguageSetting;
    password: string;
    confirmPassword: string;
}

export const Settings = memo(() => {
    const currentUser = useCurrentUser();
    const renewToken = useRenewToken();
    const intl = useIntl();

    const { refetch } = useQueryGetRefreshAndRetrieveUserConfiguration({});

    const { mutateAsync: patchUser } = useMutationPatchUpdateUserSettings({
        onError: ({ response }) => {
            toast(
                intl.formatMessage(
                    {
                        id: `requestForm.error.${(response?.data as any).errorKey}`,
                        defaultMessage: intl.formatMessage({ id: 'requestForm.error.default' }),
                    },
                    { message: (response?.data as any).message || (response?.data as any).errorKey || '' },
                ),
                errorToastConfig,
            );
        },
        onSuccess: () => {
            toast(<FormattedMessage id={'settings.profileUpdated'} />, successToastConfig);
            refetch();
        },
    });

    const initialValues: Partial<UserFormData> = {
        password: '',
        confirmPassword: '',
        language: currentUser?.settings?.language || LanguageSetting.FRENCH,
    };

    const onSubmit = useCallback(
        async (values: UserFormData) => {
            await patchUser({
                userSettingsUpdateDto: {
                    phone: currentUser.phone || '',
                    settings: {
                        language: values.language,
                        codeMode: currentUser.settings.codeMode || CodeMode.IATA,
                    },
                    ...(values.password && values.confirmPassword ? { password: values.password } : {}),
                },
            }).then(() => renewToken());
        },
        [patchUser, currentUser.phone, currentUser.settings.codeMode, renewToken],
    );

    const { formatMessage } = useIntl();
    const validate = useCallback(
        values => {
            const errors: any = {};
            if (
                values.password.length &&
                !values.password.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"|,.<>/?])\S{12,}$/)
            ) {
                errors.password = formatMessage({ id: 'settings.passwordPolicy' });
            }
            if (values.password.length && values.password !== values.confirmPassword) {
                errors.confirmPassword = formatMessage({ id: 'settings.passwordAreNotTheSame' });
            }
            return errors;
        },
        [formatMessage],
    );

    return (
        <StyledSettingsContainer>
            <StyledSettingsForm>
                <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                    <Form>
                        <SettingsForm
                            prefilledUserData={{
                                firstName: currentUser?.firstName || '',
                                lastName: currentUser?.lastName || '',
                                email: currentUser?.email || '',
                                phone: currentUser?.phone || '',
                            }}
                        />
                    </Form>
                </Formik>
            </StyledSettingsForm>
        </StyledSettingsContainer>
    );
});
